// ============================================================
// ANSICHTSSACHE-AGENT – Sortieren einer Liste
// ============================================================
// Jede Tabellenliste sortiert per Klick auf den Spaltenkopf
// (`components/liste/sortable-head.tsx`). Diese Datei ist das,
// was dabei ueberall gleich ist: die Sortierung aus der Adresse
// lesen und die Zeilen danach ordnen. Rein, ohne Datenbank — sie
// laeuft auf dem Server, wo sortiert wird, und ihre Typen im
// Browser.
//
// Je Liste bleibt nur, was fachlich ist:
//
//   view.ts / ansicht.ts   SORT_COLUMNS (Name, erste Richtung)
//                          und die Vorgabe (DEFAULT_SORT)
//   page.tsx               je Spalte der Wert, nach dem sie
//                          ordnet, dann `sortRows(...)` VOR dem
//                          Sichtfenster
//
// ZWEI SCHLUESSEL IN DER ADRESSE: `sortierung` (die Spalte) und
// `richtung` (`auf`/`ab`). Beide bleiben deutsch — die Adresse
// ist Oberflaeche, wie die Spaltenschluessel selbst
// (`?sortierung=kunde`). Fehlt die Richtung, gilt die, mit der
// die Spalte beim ersten Klick anfaengt — Text bei A, Zahl und
// Datum beim groessten bzw. neuesten. Beide Schluessel stehen in
// `SHAPE_KEYS` (`lib/saved-views.ts`) und fahren in gespeicherten
// Ansichten mit.
//
// Belegt eine Liste einen der beiden Schluessel schon fuer einen
// Filter — Banking nutzt `richtung` fuer Eingang/Ausgang —, gibt
// sie eigene Namen mit (`SortParams`, an `readSort` UND an
// `SortableHeaderRow`). Ein Schluessel fuer zweierlei hiesse: der
// Klick auf einen Spaltenkopf loescht still den Filter.
//
// LEER STEHT HINTEN, in beiden Richtungen — wie in jeder
// Tabellenkalkulation. „Ohne Faelligkeit“ ist keine besonders
// fruehe und keine besonders spaete Faelligkeit, sondern keine;
// wer umdreht, will die Werte umgedreht sehen und nicht die
// Luecken zuerst.
// ============================================================

/** Unter welchen Schluesseln die Sortierung in der Adresse steht. */
export type SortParams = { column: string; direction: string };

/** Die Schluessel fuer alle Listen, die sie nicht anders brauchen. */
export const SORT_PARAMS: SortParams = { column: "sortierung", direction: "richtung" };

/** Die Richtung, wie sie in der Adresse steht. */
export type SortDirection = "auf" | "ab";

export type SortState<K extends string = string> = { column: K; direction: SortDirection };

/** Was eine Liste je Spalte ueber das Sortieren weiss. */
export type SortColumns<K extends string> = Record<K, { label: string; first: SortDirection }>;

/** Wonach eine Spalte ordnet. Leer (`null`, `undefined`, `""`) steht immer hinten. */
export type SortValue = string | number | Date | boolean | null | undefined;

/**
 * Wie eine Spalte ihre Zeilen ordnet: meist ein Wert je Zeile,
 * fuer Sonderfaelle ein eigener Vergleich (aufsteigend gedacht —
 * `sortRows` dreht ihn fuer „ab“ selbst um).
 */
export type SortOrder<T> = ((row: T) => SortValue) | { compare: (a: T, b: T) => number };

/**
 * Liest die Sortierung aus den Suchparametern.
 *
 * Unbekannte Werte fallen auf die Vorgabe zurueck statt zu
 * scheitern: eine Adresse aus einer aelteren Fassung soll eine
 * Liste zeigen und keinen Fehler.
 */
export function readSort<K extends string>(
  params: Record<string, string | string[] | undefined>,
  columns: SortColumns<K>,
  fallback: K,
  keys: SortParams = SORT_PARAMS,
): SortState<K> {
  const one = (name: string) => {
    const value = params[name];
    return (Array.isArray(value) ? value[0] : value) ?? "";
  };
  const raw = one(keys.column);
  const column = Object.hasOwn(columns, raw) ? (raw as K) : fallback;
  const direction = one(keys.direction);
  return {
    column,
    direction: direction === "auf" || direction === "ab" ? direction : columns[column].first,
  };
}

const TEXT = new Intl.Collator("de", { numeric: true, sensitivity: "base" });

function isEmpty(value: SortValue): boolean {
  return (
    value === null ||
    value === undefined ||
    value === "" ||
    (value instanceof Date && isNaN(value.getTime()))
  );
}

/** Zwei nicht leere Werte, aufsteigend. Zahlen im Text als Zahlen: „RE-9“ vor „RE-10“. */
function compareValues(
  a: Exclude<SortValue, null | undefined>,
  b: Exclude<SortValue, null | undefined>,
) {
  if (typeof a === "string" && typeof b === "string") return TEXT.compare(a, b);
  const asNumber = (value: typeof a) => (value instanceof Date ? value.getTime() : Number(value));
  return asNumber(a) - asNumber(b);
}

/**
 * Die Zeilen in der Reihenfolge der Sortierung — als neue Liste.
 *
 * `tieBreak` entscheidet, wenn die Spalte zwei Zeilen gleich
 * findet (derselbe Kunde, dasselbe Datum), und gilt unabhaengig
 * von der Richtung: innerhalb eines Kunden steht dann immer das
 * Neueste oben, egal ob die Kunden auf- oder absteigend laufen.
 * Ohne ihn bleibt die Reihenfolge, in der die Zeilen kamen.
 */
export function sortRows<T, K extends string>(
  rows: readonly T[],
  sort: SortState<K>,
  orders: Record<K, SortOrder<T>>,
  tieBreak?: (a: T, b: T) => number,
): T[] {
  const order = orders[sort.column];
  const sign = sort.direction === "auf" ? 1 : -1;

  function byColumn(a: T, b: T): number {
    if (typeof order !== "function") return sign * order.compare(a, b);
    const va = order(a);
    const vb = order(b);
    const ea = isEmpty(va);
    const eb = isEmpty(vb);
    if (ea || eb) return ea === eb ? 0 : ea ? 1 : -1;
    return sign * compareValues(va!, vb!);
  }

  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => byColumn(a.row, b.row) || (tieBreak?.(a.row, b.row) ?? 0) || a.index - b.index)
    .map((entry) => entry.row);
}
